/** A random UUID v4; falls back to `getRandomValues` where `randomUUID` is absent (insecure contexts). */
export function randomUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The URL namespace of RFC 4122 §4.3, the one `purchaseEventId` hangs its names from. */
const NAMESPACE_URL = "6ba7b811-9dad-11d1-80b4-00c04fd430c8";

/** SHA-1 of a byte array, synchronous and dependency-free (`crypto.subtle` is async, and `track` is not). */
function sha1(message: Uint8Array): Uint8Array {
  const rotl = (value: number, bits: number) => ((value << bits) | (value >>> (32 - bits))) >>> 0;
  const length = message.length;
  const paddedLength = (((length + 8) >> 6) + 1) << 6;
  const padded = new Uint8Array(paddedLength);
  padded.set(message);
  padded[length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor((length * 8) / 0x100000000));
  view.setUint32(paddedLength - 4, (length * 8) >>> 0);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const words = new Uint32Array(80);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) words[index] = view.getUint32(offset + index * 4);
    for (let index = 16; index < 80; index += 1) {
      words[index] = rotl(words[index - 3] ^ words[index - 8] ^ words[index - 14] ^ words[index - 16], 1);
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;

    for (let index = 0; index < 80; index += 1) {
      let f: number;
      let k: number;
      if (index < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (index < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (index < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const next = (rotl(a, 5) + f + e + k + words[index]) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = next;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const digest = new Uint8Array(20);
  const out = new DataView(digest.buffer);
  [h0, h1, h2, h3, h4].forEach((word, index) => out.setUint32(index * 4, word));

  return digest;
}

/**
 * A name-based UUID (version 5, RFC 4122 §4.3): the same `namespace` and `name` always give the same
 * UUID, in any language that implements the RFC. That is what lets two systems that never talk to
 * each other name one fact the same way.
 */
export function uuidV5(name: string, namespace: string = NAMESPACE_URL): string {
  const namespaceBytes = Uint8Array.from(namespace.replace(/-/g, "").match(/.{2}/g) ?? [], (pair) => parseInt(pair, 16));
  const nameBytes = new TextEncoder().encode(name);
  const input = new Uint8Array(namespaceBytes.length + nameBytes.length);
  input.set(namespaceBytes);
  input.set(nameBytes, namespaceBytes.length);

  const bytes = sha1(input).slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The `event_id` of the `purchase`, derived from the order so the browser and the server arrive at
 * the same value without exchanging anything (INST §10, "Servidor": the backend sends `purchase`
 * through the Measurement Protocol "com o mesmo `event_id`, para o GA4 deduplicar").
 *
 * The rule, which the backend implements as is (document 19, item 43):
 * `uuidV5("https://taniabulhoes.com.br/events/purchase/" + order.id)`, namespace URL, where
 * `order.id` is the order's UUID (`orderId`), lower case.
 */
export function purchaseEventId(orderId: string): string {
  return uuidV5(`https://taniabulhoes.com.br/events/purchase/${orderId.toLowerCase()}`);
}
